import * as React from 'react';

import cx from 'classnames';

import warning from '~shared/utils/warning';

import { TAB_LIST_UPDATED } from './actions';
import { TabProps } from './Tab';
import { TabsContext } from './TabsProvider';

export type TabsOrientation = 'horizontal' | 'vertical';

export interface TabListProps {
  children: React.ReactElement<TabProps>[];
  label: string;
  orientation?: TabsOrientation;
}

interface TabListContextValues {
  tabListItemIndex: number;
  scrollContainer: HTMLElement | null;
  orientation: TabsOrientation;
}

export const TabListContext = React.createContext<TabListContextValues | null>(
  null
);

/**
 * Follows the [WAI-AIRA Tabs pattern](https://www.w3.org/TR/wai-aria-practices-1.2/#tabpanel).
 *
 * All tab components must be nested within a `TabsProvider` which provides the
 * state. Somewhere within `TabsProvider` you need to have:
 *
 *  - Provide a `TabList` component containing multiple `Tab` components
 *  - Provide a `TabPanels` component containing multiple `TabPanel` components
 *
 * The `TabList` and `TabPanels` components can be positioned wherever you like,
 * as long as they are within the `TabsProvider`.
 *
 * State is managed internally by default. If you’d like to manage tab state
 * from the outside, you must pass an `item` prop to each `Tab` element, as well
 * as `selectedItem` and `onChange` props to `TabsProvider`.
 *
 * @deprecated Use the TabsV2 components instead.
 */
export function TabList(props: TabListProps) {
  const { children, label, orientation = 'horizontal' } = props;
  const tabsContext = React.useContext(TabsContext);
  const tabsRef = React.useRef<HTMLDivElement>(null);

  warning(
    tabsContext !== null,
    'TabList must be rendered as a child of TabsProvider.'
  );

  if (!tabsContext) {
    throw new Error('TabList rendered outside TabsProvider');
  }

  const { dispatch, a11y } = tabsContext;
  const tabItems: Array<string | number> = [];

  const tabs = React.Children.toArray(children)
    .filter((child) => child)
    .map((tab: React.ReactElement, index) => {
      /**
       * @todo Re-enable this when components can "extend" the `Tab` component.
       */
      // warning(
      //   typeof tab === 'object' && tab.type === Tab,
      //   'Only Tab elements can be direct children of a TabList'
      // );

      tabItems.push(tab.props.item ?? index);

      return (
        <TabListContext.Provider
          key={index}
          value={{
            tabListItemIndex: index,
            scrollContainer: tabsRef.current,
            orientation,
          }}
        >
          {tab}
        </TabListContext.Provider>
      );
    });

  React.useEffect(() => {
    dispatch({ type: TAB_LIST_UPDATED, tabItems });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabItems.join(), dispatch]);

  const scrollContainerClass = cx(
    'flex flex-nowrap scrolling-touch',
    'tabs-scroller',
    {
      'overflow-x-auto overflow-y-hidden': orientation === 'horizontal',
      'h-full overflow-x-hidden overflow-y-auto': orientation === 'vertical',
    }
  );
  const tabListClass = React.useMemo(
    () =>
      cx('flex flex-no-wrap', {
        'flex-col w-full': orientation === 'vertical',
      }),
    [orientation]
  );

  return (
    <div className="relative w-full">
      <div ref={tabsRef} className={scrollContainerClass}>
        <div
          className={tabListClass}
          {...a11y.tabListProps({ label, orientation })}
        >
          {tabs}
        </div>
      </div>
    </div>
  );
}
